import { action, observable, toJS } from 'mobx';
import { ReactNode } from 'react';

type PageData = {
  id: string;
  type: string;
  node: ReactNode;
};

export type PageKeyData = Omit<PageData, 'node'>;

type RandomKeyType = {
  [pageKey: string]: string;
};

export const getPageKey = (data: PageKeyData) => `${data.id}_${data.type}`;

class MultiRoutePageStore {
  private pageRecord: Record<string, PageData> = {};

  /*
   * 页面跳转路由会触发多次routes/page/Page.tsx的renderComponent
   * 为了避免页面多次渲染，给页面组件添加key来
   */
  randomKey: RandomKeyType = {
    default: new Date().getTime().toString(),
  };

  @observable pageKeys: Record<string, string[]> = {};

  @observable activePageKey: string = '';

  @observable appId: string = ''; // 应用id

  @observable prePageKey: string = '';

  has = (data: PageKeyData) => !!this.pageRecord[getPageKey(data)];

  isActived = (data: PageKeyData) => this.activePageKey === getPageKey(data);

  /**
   * 将新的路由页面放到多页面列表中
   */
  push = (data: PageData) => {
    const pageKey = getPageKey(data);

    if (!this.pageRecord[pageKey]) {
      this.replace(data);
    }
  };

  renderPage = (pageKey: string) => this.pageRecord[pageKey].node;

  @action
  setActivePage(data: PageKeyData) {
    const pageKey = getPageKey(data);

    if (this.activePageKey !== pageKey) {
      this.activePageKey = pageKey;
    }
  }

  getRandomKey(pageKey: string) {
    return this.randomKey[pageKey] || this.randomKey.default;
  }

  resetRandomKey(pageKey: string) {
    this.randomKey[pageKey] = new Date().getTime().toString();
  }

  /**
   * 当页面已存在时，则替换原有的数据
   */
  @action
  replace(data: PageData) {
    const pageKey = getPageKey(data);
    const hasPage = !!this.pageRecord[pageKey];

    const pageKeys = toJS(this.pageKeys);

    this.pageRecord[pageKey] = data;

    if (!hasPage) {
      if (pageKeys[this.appId]) {
        pageKeys[this.appId].push(pageKey);
      } else {
        pageKeys[this.appId] = [pageKey];
      }
    }

    this.pageKeys = pageKeys;
  }

  @action.bound
  setAppid(appId: string) {
    this.appId = appId;
  }
}

const multiRoutePageStore = new MultiRoutePageStore();

export default multiRoutePageStore;
